import React from 'react';
import { v4 as uuid } from 'uuid';
import { verificatieBerekeningUnitUnityCheckSchema } from '../../../../common/src/domain/customInspectionTypes/verificatieBerekening/verificatieBerekeningUnitSchema';
import {
    UnitVerificatieBerekeningData,
    UnitVerificatieBerekeningUnityCheck,
} from '../../../../common/src/domain/verificatieBerekening/verificatieBerekeningPaths';
import { IconRemoveButton } from '../../components/common/iconButtons/IconRemoveButton';
import {
    CollapsiblePanel,
    EditPanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelRow,
    EditPanelTextArea,
    PanelBody,
    PanelHeader,
    PanelTable,
    TableBody,
    TableData,
    TableHead,
    TableHeadRow,
    TableRow,
    Tray,
} from '../../components/elements';
import { NullableFormattedNumber } from '../../components/utils/NullableFormattedNumber';
import { showSavedToast } from '../../components/utils/toastMessages';
import { useReactAsync } from '../../components/utils/useReactAsync';
import {
    ValidatingCulturedFormInputNumber,
    ValidatingEditPanelSaveButton,
    ValidatingFilterDropdown,
    ValidatingForm,
    ValidatingFormDependentSection,
    ValidatingFormInputText,
} from '../../components/validatingElements';
import { useTranslate } from '../../mixins/translate/translate';
import { openApiFetchGet, openApiFetchPost } from '../../store/openApiFetch';
import { SelectedOptionToText } from './SelectedOptionToText';
import { SelectedOptionToTextWithOther } from './SelectedOptionToTextWithOther';
import { VerificatieBerekeningScoreLabel } from './VerificatieBerekeningScoreLabel';
import { verificatieBerekeningSelectOptions } from './verificatieBerekeningSelectOptions';

async function loadInspectionStandardData({ surveyId, unitId }: { surveyId: string; unitId: string }) {
    const result = await openApiFetchGet('/api/surveys/{surveyId}/units/{unitId}/inspectionStandardData/verificatieBerekening', { surveyId, unitId });
    return result;
}

async function loadElementGroupModelleerNiveau({ surveyId, unitId }: { surveyId: string; unitId: string }) {
    const result = await openApiFetchGet('/api/surveys/{surveyId}/units/{unitId}/elementGroups/inspectionStandardData/verificatieBerekening', {
        surveyId,
        unitId,
    });

    return result.modelleerNiveau ?? '0';
}

export function SurveyEditVerificatieBerekeningAnalysisUnit(props: {
    unitId: string;
    unitName: string;
    surveyId: string;
    onItemChanged: () => Promise<void>;
}) {
    const translate = useTranslate();
    const [isAddEditUnityCheckVisible, setIsAddEditUnityCheckVisible] = React.useState(false);
    const [editingUnityCheckId, setEditingUnityCheckId] = React.useState<string | undefined>();
    const [opmerking, setOpmerking] = React.useState('');
    const { data, reload } = useReactAsync(loadInspectionStandardData, { surveyId: props.surveyId, unitId: props.unitId }, [props.unitId]);
    const { data: defaultModelleerNiveau } = useReactAsync(loadElementGroupModelleerNiveau, { surveyId: props.surveyId, unitId: props.unitId }, [
        props.unitId,
    ]);

    React.useEffect(() => {
        setOpmerking(data?.opmerking ?? '');
    }, [data]);

    async function addEditUnityCheck(formData: { item: UnitVerificatieBerekeningUnityCheck }) {
        const newData: UnitVerificatieBerekeningData = { ...data! };

        if (editingUnityCheckId) {
            const index = newData.unityChecks.findIndex((i) => i.id === editingUnityCheckId);
            newData.unityChecks[index] = formData.item;
        } else {
            newData.unityChecks.push(formData.item);
        }

        await updateData(newData);

        return { id: formData.item.id };
    }

    async function removeUnityCheck(id: string) {
        await updateData({ ...data, ...{ unityChecks: data!.unityChecks.filter((i) => i.id !== id) } });
    }

    async function saveOpmerking() {
        const newData = { ...data!, ...{ opmerking } };
        await updateData(newData);
        showSavedToast(translate);
    }

    async function updateData(newData: UnitVerificatieBerekeningData) {
        await openApiFetchPost(
            '/api/surveys/{surveyId}/units/{unitId}/inspectionStandardData/verificatieBerekening',
            { surveyId: props.surveyId, unitId: props.unitId },
            newData,
        );
        await props.onItemChanged();
        await reload();
    }

    if (!data) {
        return null;
    }

    return (
        <Tray>
            <EditPanel bottomMargin={true} noPadding={true}>
                <CollapsiblePanel heading={{ dark: true, text: `Verificatie berekening: ${props.unitName}` }}>
                    <PanelBody isForm={true}>
                        <PanelTable hover={true}>
                            <TableHead>
                                <TableHeadRow>
                                    <TableData>Type</TableData>
                                    <TableData>Modelleer niveau</TableData>
                                    <TableData>Unity check</TableData>
                                    <TableData />
                                    <TableData />
                                </TableHeadRow>
                            </TableHead>
                            <TableBody>
                                {data &&
                                    data.unityChecks &&
                                    data.unityChecks.map((unityCheck) => (
                                        <TableRow
                                            key={unityCheck.id}
                                            isRowSelected={unityCheck.id === editingUnityCheckId}
                                            onClick={() => {
                                                if (isAddEditUnityCheckVisible) {
                                                    setEditingUnityCheckId(undefined);
                                                    setIsAddEditUnityCheckVisible(false);
                                                    setTimeout(() => {
                                                        setEditingUnityCheckId(unityCheck.id);
                                                        setIsAddEditUnityCheckVisible(true);
                                                    }, 100);
                                                } else {
                                                    setEditingUnityCheckId(unityCheck.id);
                                                    setIsAddEditUnityCheckVisible(true);
                                                }
                                            }}
                                        >
                                            <TableData>
                                                <SelectedOptionToTextWithOther
                                                    selectedValue={unityCheck.unityCheckType}
                                                    options={verificatieBerekeningSelectOptions.unityCheckType}
                                                    otherId="23"
                                                    otherValue={unityCheck.unityCheckTypeAnders}
                                                />{' '}
                                            </TableData>
                                            <TableData>
                                                <SelectedOptionToText
                                                    selectedValue={unityCheck.modelleerNiveau}
                                                    options={verificatieBerekeningSelectOptions.modelleerNiveau}
                                                />
                                            </TableData>
                                            <TableData>
                                                <NullableFormattedNumber value={unityCheck.unityCheckScore} />
                                            </TableData>
                                            <TableData>
                                                <VerificatieBerekeningScoreLabel score={unityCheck.score} />
                                            </TableData>
                                            <TableData>
                                                <IconRemoveButton
                                                    removeQuestion={translate('common.confirmDeleteItem')}
                                                    onClick={async () => removeUnityCheck(unityCheck.id)}
                                                />
                                            </TableData>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </PanelTable>
                        {isAddEditUnityCheckVisible ? (
                            <>
                                <EditPanel border={true}>
                                    <ValidatingForm
                                        schema={verificatieBerekeningUnitUnityCheckSchema}
                                        onSubmitted={() => {
                                            showSavedToast(translate);
                                            setIsAddEditUnityCheckVisible(false);
                                        }}
                                        defaultItem={
                                            editingUnityCheckId
                                                ? data.unityChecks.find((i) => i.id === editingUnityCheckId)
                                                : { id: uuid(), modelleerNiveau: defaultModelleerNiveau }
                                        }
                                        overrideSubmit={async (formData) => await addEditUnityCheck(formData)}
                                    >
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={4}>
                                                <ValidatingFilterDropdown
                                                    name="unityCheckType"
                                                    selectOptions={verificatieBerekeningSelectOptions.unityCheckType}
                                                    topLabel="Type"
                                                />
                                                <ValidatingFormDependentSection showIf={(item) => item.unityCheckType === '23'}>
                                                    <ValidatingFormInputText name="unityCheckTypeAnders" />
                                                </ValidatingFormDependentSection>
                                            </EditPanelCell>
                                            <EditPanelCell rowCount={4}>
                                                <ValidatingFilterDropdown
                                                    name="modelleerNiveau"
                                                    selectOptions={verificatieBerekeningSelectOptions.modelleerNiveau}
                                                    topLabel="Modelleer niveau"
                                                />
                                            </EditPanelCell>
                                            <EditPanelCell rowCount={4}>
                                                <ValidatingCulturedFormInputNumber
                                                    name="unityCheckScore"
                                                    topLabel="Unity check"
                                                    placeholder="Unity check"
                                                />{' '}
                                            </EditPanelCell>
                                        </EditPanelRow>
                                        <EditPanelButtons>
                                            <EditPanelButton small={true} text="Annuleer" onClick={() => setIsAddEditUnityCheckVisible(false)} />
                                            <ValidatingEditPanelSaveButton small={true} />
                                        </EditPanelButtons>
                                    </ValidatingForm>
                                </EditPanel>
                            </>
                        ) : (
                            <>
                                <EditPanelButtons>
                                    <EditPanelButton
                                        onClick={() => {
                                            setEditingUnityCheckId(undefined);
                                            setIsAddEditUnityCheckVisible(true);
                                        }}
                                        iconClass="fa-plus"
                                        text="Toetsing toevoegen"
                                        small
                                    />
                                </EditPanelButtons>
                            </>
                        )}

                        <PanelHeader text="Opmerking" />
                        <EditPanelRow fixWidth={true}>
                            <EditPanelCell rowCount={12}>
                                <EditPanelTextArea value={opmerking} handleChange={(value) => setOpmerking(value)} name="opmerking" />
                            </EditPanelCell>
                        </EditPanelRow>
                        <EditPanelButtons fixWidth={true}>
                            <EditPanelButton small onClick={saveOpmerking} text="Opslaan" primary />
                        </EditPanelButtons>
                    </PanelBody>
                </CollapsiblePanel>
            </EditPanel>
        </Tray>
    );
}
